// @flow
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import * as React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { connectAPI, withConfirmation } from 'speed-js-react';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import ConnectIcon from 'esm-web-pro/components/layout/sidePanel/icons/ConnectIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import cableAndFiberMessages from 'esm-web-pro/messages/cableAndFiberMessages';
import { withLeaflet } from 'react-leaflet';
import FiberDamageAction from 'esm-web-pro/containers/pages/network/objects/fiber/FiberDamageAction';
import type { Map } from 'esm-web-pro/containers/map/types';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import type { VisualisedFiber } from 'esm-web-pro/containers/pages/network/objects/cable/FiberList';
import type { LeafletContext } from 'react-leaflet/lib/types';
import {
    fiberCircuitCallback, CableFiberColor, FiberTextContainer,
} from 'esm-web-pro/containers/pages/network/objects/cable/CableFiber';
import { POINT12_CONTENT_TOOLTIP_KEY } from './CableList/Point12CableList';

type Props = {
    cable: Object,
    fiber: Object,
    fiberSequence: number,
    onConnect: Function,
    onDamage: Function,
    map: Map,
    intl: IntlShape,
    leaflet: LeafletContext,
    onView: Function,
    setClickedRowIdentity: Function,
    clickedRowIdentity: string,
    highlightFiber: Function,
    getFiberCircuitAndPoints: (uuid: string, map: Map) => Promise<Object>,
    shouldDisableSplicing?: boolean,
    shouldShowSpliceIconOnFreeFiberRow?: boolean
}

const FreeFiberRow = (props: Props) => {
    const {
        cable, fiberSequence, onConnect, map: { is_draft: mapIsDraft }, map, intl, leaflet, onView,
        setClickedRowIdentity, clickedRowIdentity, fiber, onDamage, map: networkMap, highlightFiber,
        getFiberCircuitAndPoints, shouldDisableSplicing, shouldShowSpliceIconOnFreeFiberRow,
    } = props;

    const handleDamage = () => {
        onDamage();
    };

    const visualise = (e) => {
        getFiberCircuitAndPoints(fiber.uuid, map).then((vFiber: VisualisedFiber) => {
            const [callbacks, circuitPath] = highlightFiberCircuit(
                vFiber.path, vFiber, leaflet.map, highlightFiber,
            );
            onView(e, callbacks, circuitPath);
        });
    };

    const rowId = `C${cable.id}F${fiberSequence}`;

    return (
        <ContentRow
            hoverIndicator="none"
            padLevel={2}
            onClick={(e) => {
                setClickedRowIdentity(`C${cable.id}F${fiberSequence}`);
                visualise(e);
            }}
            object={cable}
            rowId={rowId}
            clickedRowIdentity={clickedRowIdentity}
            actions={mapIsDraft
                ? (
                    <>
                        <FiberDamageAction
                            fiber={fiber}
                            map={networkMap}
                            updateObject={handleDamage}
                            actionProps={{
                                size: '20px',
                                width: '3px',
                                tooltip: true,
                            }}
                            pad={shouldShowSpliceIconOnFreeFiberRow ? { right: '2px' } : { right: '15px' }}
                            ActionComponent={RowActions}
                        />
                        {shouldShowSpliceIconOnFreeFiberRow && (
                            <RowActions
                                gapBetweenActions="small"
                                actions={[
                                    {
                                        key: `${cable.id}-${fiberSequence}-connect`,
                                        icon: <ConnectIcon />,
                                        callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                                            e.stopPropagation();
                                            onConnect({ cable, fiberSequence });
                                        },
                                        size: '20px',
                                        width: '3px',
                                        tooltip: intl.formatMessage(actionMessages.splice),
                                        tooltipKey: POINT12_CONTENT_TOOLTIP_KEY,
                                        disabled: shouldDisableSplicing,
                                    },
                                ]}
                            />
                        )}
                    </>
                ) : []}
        >
            <FiberTextContainer>
                {(fiber.attributes?.color) && (
                    <CableFiberColor
                        color={fiber.attributes.color}
                        stripeCount={fiber.attributes.stripe_count}
                        tubeNumber={fiber.attributes.tube_number}
                    />
                )}
                {`${intl.formatMessage(cableAndFiberMessages.fiberLong)} ${fiberSequence}`}
            </FiberTextContainer>
        </ContentRow>
    );
};

FreeFiberRow.defaultProps = {
    shouldDisableSplicing: false,
    shouldShowSpliceIconOnFreeFiberRow: true,
};

export default withLeaflet(injectIntl(withConfirmation(connectAPI(fiberCircuitCallback)(FreeFiberRow))));
